import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ChevronLeft, ChevronRight, Plus, Settings2 } from 'lucide-react';
import type { LeaveBalanceDto, LeaveCalendarEntry, LeaveRequestDto, Paginated } from '@workforce/shared';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useLeaveTypes } from '@/lib/leave';
import { useEmployeeOptions } from '@/lib/queries';
import { t } from '@/lib/i18n';
import { LeaveRequestForm } from '@/components/LeaveRequestForm';
import { LeaveRequestList } from '@/components/LeaveRequestList';
import { Button, Card, ErrorBanner, Field, Modal, PageHeader, Pagination, Select, Spinner, Tabs } from '@/components/ui';

type Tab = 'requests' | 'calendar';

export function LeaveAdminPage() {
  const { can, user } = useAuth();
  const [tab, setTab] = useState<Tab>('requests');
  const [recordOpen, setRecordOpen] = useState(false);
  const isAdmin = can('leave.balances.adjust');
  return (
    <div>
      <PageHeader
        title={user?.role === 'manager' ? t('Leave approvals') : t('Leave')}
        subtitle={user?.role === 'manager' ? t('Requests from your team') : t('All leave requests across the company')}
        actions={
          isAdmin && (
            <>
              <Link to="/leave/setup">
                <Button variant="secondary" icon={<Settings2 className="h-4 w-4" />}>
                  {t('Leave types & policies')}
                </Button>
              </Link>
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => setRecordOpen(true)}>
                {t('Record leave')}
              </Button>
            </>
          )
        }
      />
      <Tabs
        tabs={[
          { value: 'requests', label: t('Requests') },
          { value: 'calendar', label: t('Calendar') },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'requests' ? <RequestsTab /> : <CalendarTab />}
      {recordOpen && <RecordLeaveModal onClose={() => setRecordOpen(false)} />}
    </div>
  );
}

function RequestsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [page, setPage] = useState(1);
  const types = useLeaveTypes();
  const people = useEmployeeOptions();
  const q = useQuery({
    queryKey: ['leave', 'requests', { status, leaveTypeId, employeeId, page }],
    queryFn: () => api.get<Paginated<LeaveRequestDto>>(`/leave/requests${qs({ status, leaveTypeId, employeeId, page, pageSize: 30 })}`),
    placeholderData: keepPreviousData,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['leave'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  return (
    <>
      <div className="card mb-4 grid grid-cols-1 gap-3 p-4 sm:grid-cols-3">
        <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label={t('Status')}>
          <option value="pending">{t('Pending approval')}</option>
          <option value="approved">{t('Approved')}</option>
          <option value="rejected">{t('Rejected')}</option>
          <option value="cancelled">{t('Cancelled')}</option>
          <option value="">{t('All statuses')}</option>
        </Select>
        <Select value={leaveTypeId} onChange={(e) => { setLeaveTypeId(e.target.value); setPage(1); }} aria-label={t('Leave type')}>
          <option value="">{t('All leave types')}</option>
          {types.data?.map((lt) => (
            <option key={lt.id} value={lt.id}>
              {t(lt.name)}
            </option>
          ))}
        </Select>
        <Select value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setPage(1); }} aria-label={t('Employee')}>
          <option value="">{t('All employees')}</option>
          {people.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.fullName}
            </option>
          ))}
        </Select>
      </div>
      <ErrorBanner error={q.error} />
      <Card bodyClass="p-0">
        {q.isLoading ? (
          <Spinner />
        ) : (
          <>
            <LeaveRequestList items={q.data?.items ?? []} onChanged={refresh} empty={status === 'pending' ? t('Nothing waiting for approval 🎉') : t('No requests match')} />
            <Pagination page={page} pageSize={30} total={q.data?.total ?? 0} onPage={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

function CalendarTab() {
  const [month, setMonth] = useState(() => DateTime.now().startOf('month'));
  const gridStart = month.startOf('week'); // Monday
  const gridEnd = month.endOf('month').endOf('week');
  const q = useQuery({
    queryKey: ['leave', 'calendar', month.toISODate()],
    queryFn: () => api.get<LeaveCalendarEntry[]>(`/leave/calendar?from=${gridStart.toISODate()}&to=${gridEnd.toISODate()}`),
  });
  const byDate = useMemo(() => {
    const m = new Map<string, LeaveCalendarEntry[]>();
    for (const e of q.data ?? []) m.set(e.date, [...(m.get(e.date) ?? []), e]);
    return m;
  }, [q.data]);
  const days: DateTime[] = [];
  for (let d = gridStart; d <= gridEnd; d = d.plus({ days: 1 })) days.push(d);
  const today = DateTime.now().toISODate();
  return (
    <Card
      title={month.toFormat('LLLL yyyy')}
      actions={
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setMonth(month.minus({ months: 1 }))} aria-label={t('Previous month')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMonth(DateTime.now().startOf('month'))}>
            {t('Today')}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMonth(month.plus({ months: 1 }))} aria-label={t('Next month')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      }
      bodyClass="p-0"
    >
      <ErrorBanner error={q.error} />
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50 text-center text-[11px] font-semibold uppercase text-slate-500">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
          <div key={d} className="py-2">
            {t(d)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const iso = d.toISODate()!;
          const entries = byDate.get(iso) ?? [];
          const inMonth = d.month === month.month;
          return (
            <div key={iso} className={`min-h-[92px] border-b border-e border-slate-100 p-1.5 ${inMonth ? '' : 'bg-slate-50/60'}`}>
              <p className={`mb-1 text-xs ${iso === today ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 font-semibold text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'}`}>
                {d.day}
              </p>
              <div className="space-y-0.5">
                {entries.slice(0, 4).map((e) => (
                  <p
                    key={e.requestId + e.date}
                    title={`${e.employeeName} — ${t(e.leaveTypeName)}${e.status === 'pending' ? ` ${t('(pending)')}` : ''}${e.fraction < 1 ? ` ${t('(half day)')}` : ''}`}
                    className={`truncate rounded px-1 py-0.5 text-[11px] font-medium ${e.status === 'pending' ? 'border border-dashed' : 'text-white'}`}
                    style={e.status === 'pending' ? { borderColor: e.color, color: e.color } : { backgroundColor: e.color }}
                  >
                    {e.employeeName.split(' ')[0]}
                    {e.fraction < 1 ? ' ½' : ''}
                  </p>
                ))}
                {entries.length > 4 && <p className="text-[11px] text-slate-500">{t('+{n} more', { n: entries.length - 4 })}</p>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="px-4 py-2 text-xs text-slate-500">{t('Solid = approved · dashed = waiting for approval · ½ = half day')}</p>
    </Card>
  );
}

function RecordLeaveModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const people = useEmployeeOptions();
  const types = useLeaveTypes();
  const [employeeId, setEmployeeId] = useState('');
  const detail = useQuery({
    queryKey: ['leave', 'employee', employeeId],
    queryFn: () => api.get<{ balances: LeaveBalanceDto[] }>(`/leave/employees/${employeeId}`),
    enabled: !!employeeId,
  });
  return (
    <Modal open onClose={onClose} title={t('Record leave for an employee')} size="lg">
      <LeaveRequestForm
        adminMode
        employeeId={employeeId}
        types={(types.data ?? []).filter((lt) => lt.isActive)}
        balances={detail.data?.balances}
        employeePicker={
          <Field label={t('Employee')} required>
            <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">{t('Select employee…')}</option>
              {people.data?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName} ({p.employeeCode})
                </option>
              ))}
            </Select>
          </Field>
        }
        onCancel={onClose}
        onSubmitted={() => {
          void qc.invalidateQueries({ queryKey: ['leave'] });
          void qc.invalidateQueries({ queryKey: ['dashboard'] });
          onClose();
        }}
      />
    </Modal>
  );
}
